import React, { useState } from "react";
import {
  Anchor,
  Box,
  TBody,
  THead,
  Table,
  Td,
  Text,
  Th,
  Tr,
} from "@twilio-paste/core";

import { Pages, EmptyState } from "../../components";
import { EmptyStateVariant } from "../../components/emptyState/definitions/types";
import { ChatHistoryLoader } from "./sub";
import { types, copy, constants } from "./definitions";

function getItemsOnPage<T>(
  list: T[],
  pageSize: number,
  pageNumber: number
): T[] {
  const startIndex = (pageNumber - 1) * pageSize;
  const endIndex = pageNumber * pageSize;

  if (startIndex >= list.length) {
    return [];
  }

  return list.slice(startIndex, endIndex);
}

function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  const humanReadableDate =
    date.toLocaleDateString("en-US", constants.dateOptions) +
    ", " +
    date.toLocaleTimeString("en-US", constants.timeOptions);
  return humanReadableDate;
}

const ChatHistory: React.FC<types.ChatHistoryProps> = ({
  chats,
  loading,
  onChatHistoryItemClick,
  variantOptions,
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  const onPaginate = (page: number): void => setCurrentPage(page);

  const pageCount = Math.ceil(chats.length / constants.rowsPerPage);

  const renderStateVariant = () => {
    if (!variantOptions) {
      return;
    }

    const { variant, ctaText, ctaUrl } = variantOptions;

    if (variant === EmptyStateVariant.oops) {
      return (
        <EmptyState
          variant={variant}
          heading={copy.variants.oops.title}
          description={copy.variants.oops.description}
        >
          <Anchor href={ctaUrl}>{ctaText}</Anchor>
        </EmptyState>
      );
    }

    return (
      <EmptyState
        variant={variant}
        heading={copy.variants.create.title}
        description={copy.variants.create.description}
      >
        <Anchor href={ctaUrl}>{ctaText}</Anchor>
      </EmptyState>
    );
  };

  if (loading) {
    return <ChatHistoryLoader />;
  }

  return (
    <>
      <Text as="h2" fontSize="fontSize90" marginBottom={"space100"}>
        {copy.title}
      </Text>
      <Text as="p" paddingBottom={"space40"}>
        {copy.description}
      </Text>
      {variantOptions ? (
        renderStateVariant()
      ) : (
        <>
          <Table>
            <THead>
              <Tr>
                <Th>{copy.table.headers.title}</Th>
                <Th width={"size30"}>{copy.table.headers.updatedAt}</Th>
              </Tr>
            </THead>
            <TBody>
              {getItemsOnPage(chats, constants.rowsPerPage, currentPage).map(
                (chat) => (
                  <Tr key={chat.id}>
                    <Td>
                      {onChatHistoryItemClick ? (
                        <Anchor
                          href="#"
                          onClick={() => onChatHistoryItemClick(chat.id)}
                        >
                          {chat.title}
                        </Anchor>
                      ) : (
                        <Anchor href={chat.url}>{chat.title}</Anchor>
                      )}
                    </Td>
                    <Td>{formatTimestamp(chat.updatedAt)}</Td>
                  </Tr>
                )
              )}
            </TBody>
          </Table>
          <Pages
            currentPage={currentPage}
            onPaginate={onPaginate}
            pageCount={pageCount}
          />
        </>
      )}
    </>
  );
};

export default ChatHistory;
